import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LeadCloudFileType } from '@econtractor/types';
import * as apiProductSet from 'api/apiProductSets';
import { createPdfBlob } from 'utils/helpers/pdf/pdfMake';
import * as apiSupplierRequests from 'api/apiSupplierRequests';
import { getCloudflareUploadUrls } from 'api/apiCloudflare';
import { useSettings } from 'context/SettingsContext';
import { useToast } from 'context/ToastContext';
import { uploadFilesWithOverallProgress } from 'utils/helpers/featchFiles/uploadFileWithProgress';
import { buildSupplierRequestMailContent } from 'utils/helpers/email/buildMailContent';
import { safeFloatParse, safeIntParse } from 'utils/helpers/safeParsing';
import type { ProductSelectOption, ProductSetSelectOption } from 'modules/product';
import { SUPPLIER_REQUEST_NOTE_MAX_LENGTH } from './constants';
import { buildSupplierRequestPdfDefinition } from './supplierRequestPdfDefinition';
import type { SupplierRequestCreateDraft, SupplierRequestDraftItem } from './types';

interface UseCreateSupplierRequestParams {
    vendorId: number | null;
    initialDraft?: SupplierRequestCreateDraft | null;
    /** When set, load items/notes from this request (clone). */
    cloneFromRequestId?: number | null;
    /** Increments on each open so clone/create re-applies draft for the same vendor. */
    createKey?: number;
    onClose: () => void;
    onSaved: () => void;
}

const mapDraftItems = (
    items:
        | Array<{
              productId?: unknown;
              prodName?: unknown;
              prodArticle?: unknown;
              itemCount?: unknown;
          }>
        | undefined,
): SupplierRequestDraftItem[] =>
    (items || [])
        .map((item) => ({
            productId: safeIntParse(item.productId) || 0,
            prodName: String(item.prodName || ''),
            prodArticle: String(item.prodArticle || ''),
            itemCount: safeFloatParse(item.itemCount) || 0,
        }))
        .filter((item) => item.productId > 0 && item.itemCount > 0);

const useCreateSupplierRequest = ({
    vendorId,
    initialDraft = null,
    cloneFromRequestId = null,
    createKey = 0,
    onClose,
    onSaved,
}: UseCreateSupplierRequestParams) => {
    const { t } = useTranslation(['Warehouses', 'common']);
    const { showToast } = useToast();
    const { localSettings } = useSettings();

    const [sourceType, setSourceType] = useState<'product' | 'set'>('product');
    const [selectedProduct, setSelectedProduct] = useState<ProductSelectOption | null>(null);
    const [selectedSet, setSelectedSet] = useState<ProductSetSelectOption | null>(null);
    const [draftCount, setDraftCount] = useState(1);
    const [note, setNote] = useState('');
    const [vendorNote, setVendorNote] = useState('');
    const [sendToVendor, setSendToVendor] = useState(false);
    const [vendorEmail, setVendorEmail] = useState('');
    const [items, setItems] = useState<SupplierRequestDraftItem[]>([]);
    const [editCountIndex, setEditCountIndex] = useState<number | null>(null);
    const [isAdding, setIsAdding] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingDraft, setIsLoadingDraft] = useState(false);

    const initialDraftRef = useRef(initialDraft);
    initialDraftRef.current = initialDraft;
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    const vendor = useMemo(
        () =>
            localSettings?.DIRS?.Vendor?.find((v) => Number(v.ID) === Number(vendorId)) || null,
        [localSettings?.DIRS?.Vendor, vendorId],
    );
    const vendorName = String(vendor?.vendorName || '');
    const vendorDefaultEmail = String(vendor?.vendorEmail || '').trim();

    useEffect(() => {
        if (!vendorId) {
            setIsLoadingDraft(false);
            return;
        }

        let cancelled = false;
        const sourceId = safeIntParse(cloneFromRequestId);

        setSourceType('product');
        setSelectedProduct(null);
        setSelectedSet(null);
        setDraftCount(1);
        setSendToVendor(false);
        setVendorEmail(vendorDefaultEmail);
        setEditCountIndex(null);

        if (!sourceId) {
            const draft = initialDraftRef.current;
            setNote(String(draft?.note || '').slice(0, SUPPLIER_REQUEST_NOTE_MAX_LENGTH));
            setVendorNote(String(draft?.vendorNote || '').slice(0, SUPPLIER_REQUEST_NOTE_MAX_LENGTH));
            setItems(mapDraftItems(draft?.items));
            setIsLoadingDraft(false);
            return;
        }

        setNote('');
        setVendorNote('');
        setItems([]);
        setIsLoadingDraft(true);

        void (async () => {
            const full = await apiSupplierRequests.getById(sourceId);
            if (cancelled) return;
            setIsLoadingDraft(false);
            if (!full) {
                showToast(t('common:messError'), 'danger');
                onCloseRef.current();
                return;
            }
            const nextItems = mapDraftItems(full.items);
            if (!nextItems.length) {
                showToast(t('supplierRequests.messItemsRequired'), 'warning');
                onCloseRef.current();
                return;
            }
            setNote(String(full.note || '').slice(0, SUPPLIER_REQUEST_NOTE_MAX_LENGTH));
            setVendorNote(String(full.vendorNote || '').slice(0, SUPPLIER_REQUEST_NOTE_MAX_LENGTH));
            setItems(nextItems);
        })();

        return () => {
            cancelled = true;
        };
        // Only when open session / vendor / clone source changes
    }, [vendorId, vendorDefaultEmail, createKey, cloneFromRequestId]);

    const handleSendToVendorChange = (value: boolean) => {
        setSendToVendor(value);
        if (value && !String(vendorEmail || '').trim() && vendorDefaultEmail) {
            setVendorEmail(vendorDefaultEmail);
        }
    };

    const setNoteLimited = (value: string) => {
        setNote(String(value || '').slice(0, SUPPLIER_REQUEST_NOTE_MAX_LENGTH));
    };

    const setVendorNoteLimited = (value: string) => {
        setVendorNote(String(value || '').slice(0, SUPPLIER_REQUEST_NOTE_MAX_LENGTH));
    };

    const handleDuplicateNoteToVendor = () => {
        setVendorNote(String(note || '').slice(0, SUPPLIER_REQUEST_NOTE_MAX_LENGTH));
    };

    const upsertItems = (nextItems: SupplierRequestDraftItem[]) => {
        setItems((prev) => {
            const map = new Map(prev.map((item) => [item.productId, { ...item }]));
            for (const item of nextItems) {
                const existing = map.get(item.productId);
                if (existing) {
                    map.set(item.productId, {
                        ...existing,
                        itemCount:
                            (safeFloatParse(existing.itemCount) || 0) +
                            (safeFloatParse(item.itemCount) || 0),
                        prodName: item.prodName || existing.prodName,
                        prodArticle: item.prodArticle || existing.prodArticle,
                    });
                } else {
                    map.set(item.productId, item);
                }
            }
            return Array.from(map.values());
        });
    };

    const handleAddProduct = () => {
        const productId = safeIntParse(selectedProduct?.value) || 0;
        const count = safeFloatParse(draftCount) || 0;
        if (!productId || !(count > 0)) {
            showToast(t('supplierRequests.messInputCount'), 'warning');
            return;
        }
        upsertItems([
            {
                productId,
                prodName: String(selectedProduct?.label || ''),
                prodArticle: String(selectedProduct?.article || ''),
                itemCount: count,
            },
        ]);
        setSelectedProduct(null);
        setDraftCount(1);
    };

    const handleAddSet = async () => {
        if (isAdding) return;
        const setId = safeIntParse(selectedSet?.value) || 0;
        const qty = safeFloatParse(draftCount) || 0;
        if (!setId) return;
        if (!(qty > 0)) {
            showToast(t('supplierRequests.messInputCount'), 'warning');
            return;
        }

        setIsAdding(true);
        try {
            const resSet = await apiProductSet.getProductSet(setId);
            const vendorItems = (resSet?.items || []).filter(
                (item) => safeIntParse(item.vendorId) === safeIntParse(vendorId),
            );
            if (!vendorItems.length) {
                showToast(t('supplierRequests.messSetNoVendorItems'), 'warning');
                return;
            }
            upsertItems(
                mapDraftItems(
                    vendorItems.map((item) => ({
                        productId: item.productId,
                        prodName: item.prodName,
                        prodArticle: item.prodArticle,
                        itemCount: (safeFloatParse(item.itemCount) || 0) * qty,
                    })),
                ),
            );
            setSelectedSet(null);
            setDraftCount(1);
        } catch (e) {
            console.error(e);
            showToast(t('common:messError'), 'danger');
        } finally {
            setIsAdding(false);
        }
    };

    const handleDraftAdd = () => {
        if (sourceType === 'product') handleAddProduct();
        else void handleAddSet();
    };

    const removeItem = (index: number) => {
        setItems((prev) => prev.filter((_, i) => i !== index));
        setEditCountIndex(null);
    };

    const saveCountEdit = (index: number, value: string | number) => {
        const count = safeFloatParse(value) || 0;
        if (!(count > 0)) {
            showToast(t('supplierRequests.messInputCount'), 'warning');
            setEditCountIndex(null);
            return;
        }
        setItems((prev) =>
            prev.map((item, i) => (i === index ? { ...item, itemCount: count } : item)),
        );
        setEditCountIndex(null);
    };

    const getPdfBlobFromDefinition = async (docDefinition: unknown): Promise<Blob> => {
        try {
            return await createPdfBlob(docDefinition);
        } catch {
            throw new Error('supplier_request_pdf_blob_empty');
        }
    };

    const uploadPdf = async (file: File): Promise<LeadCloudFileType | null> => {
        const uploadConfigs = await getCloudflareUploadUrls(
            [
                {
                    fileName: file.name,
                    contentType: file.type || 'application/pdf',
                    fileSize: file.size,
                },
            ],
            {},
            { keyPrefix: 'supplier-requests' },
        );
        if (!uploadConfigs?.length) return null;
        const uploaded = await uploadFilesWithOverallProgress([file], uploadConfigs);
        const uploadedFile = uploaded?.[0];
        if (!uploadedFile?.success) return null;
        return uploadedFile.config;
    };

    const handleSave = async () => {
        if (isSaving) return;
        const resolvedVendorId = safeIntParse(vendorId) || 0;
        if (!resolvedVendorId) {
            showToast(t('supplierRequests.messVendorRequired'), 'warning');
            return;
        }
        if (!items.length) {
            showToast(t('supplierRequests.messItemsRequired'), 'warning');
            return;
        }
        const email = String(vendorEmail || '').trim();
        if (sendToVendor && !email) {
            showToast(t('supplierRequests.create.vendorEmailRequired'), 'warning');
            return;
        }

        setIsSaving(true);
        try {
            const generatedAt = Date.now();
            const noteValue = note.trim().slice(0, SUPPLIER_REQUEST_NOTE_MAX_LENGTH);
            const vendorNoteValue = vendorNote.trim().slice(0, SUPPLIER_REQUEST_NOTE_MAX_LENGTH);
            const docDefinition = buildSupplierRequestPdfDefinition({
                t: (key, options) => t(key, options || {}),
                vendorName,
                note: vendorNoteValue,
                items,
                generatedAt,
            });
            const pdfBlob = await getPdfBlobFromDefinition(docDefinition);
            const fileName = `${t('supplierRequests.pdf.fileName')}_${generatedAt}.pdf`;
            const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });
            const cloudFile = await uploadPdf(pdfFile);
            if (!cloudFile) {
                showToast(t('common:messError'), 'danger');
                return;
            }

            const result = await apiSupplierRequests.create({
                vendorId: resolvedVendorId,
                note: noteValue || null,
                vendorNote: vendorNoteValue || null,
                items,
                file: cloudFile,
                sentToEmail: sendToVendor && Boolean(email),
                sentEmail: sendToVendor ? email : '',
            });
            if (!result?.stat || !result.requestId) {
                showToast(t('common:messError'), 'danger');
                return;
            }

            if (sendToVendor && email) {
                const mailContent = await buildSupplierRequestMailContent({
                    fileUrl: cloudFile.publicUrl,
                });
                const sent = mailContent
                    ? await apiSupplierRequests.sendEmail(result.requestId, {
                          email,
                          file: cloudFile,
                          mailContent,
                      })
                    : false;
                if (!sent) {
                    showToast(t('supplierRequests.create.messEmailSendFail'), 'warning');
                }
            }

            showToast(t('supplierRequests.messCreated'), 'success');
            onSaved();
            onClose();
        } catch (e) {
            console.error(e);
            showToast(t('common:messError'), 'danger');
        } finally {
            setIsSaving(false);
        }
    };

    const handleHide = () => {
        if (isSaving) return;
        onClose();
    };

    return {
        t,
        vendorId,
        vendorName,
        sourceType,
        setSourceType,
        selectedProduct,
        setSelectedProduct,
        selectedSet,
        setSelectedSet,
        draftCount,
        setDraftCount,
        note,
        setNote: setNoteLimited,
        vendorNote,
        setVendorNote: setVendorNoteLimited,
        handleDuplicateNoteToVendor,
        sendToVendor,
        setSendToVendor: handleSendToVendorChange,
        vendorEmail,
        setVendorEmail,
        noteMaxLength: SUPPLIER_REQUEST_NOTE_MAX_LENGTH,
        items,
        editCountIndex,
        setEditCountIndex,
        isAdding,
        isSaving,
        isLoadingDraft,
        handleDraftAdd,
        removeItem,
        saveCountEdit,
        handleSave,
        handleHide,
    };
};

export default useCreateSupplierRequest;
